import React, { useState } from 'react';
import { Users } from 'lucide-react';

/**
 * CrewSummaryCard - Displays crew statistics in a summary card
 *
 * Uses the stats shape from calculateCrewStats:
 * { total, active, onLeave, offDuty, compliant, warnings, issues }
 *
 * FT-90: Implements dual-view compliance scoring
 * - SMS Compliance View: Only "On Duty" crew count (regulatory requirement)
 * - Planning View: All crew (for shore-side planning)
 *
 * MCA auditors care about CURRENT crew on board/on duty, not historical or off-duty crew.
 * This aligns with ISM Code and UK Workboat Code requirements.
 */
const CrewSummaryCard = ({ crewStats }) => {
  // FT-90: Toggle between "On Duty" (SMS Compliance) and "All Crew" (Planning) views
  const [viewMode, setViewMode] = useState('onDuty'); // 'onDuty' | 'allCrew'

  const activeCount = crewStats.active || 0;           // On Duty crew
  const offDutyCount = crewStats.offDuty || 0;
  const onLeaveCount = crewStats.onLeave || 0;
  const compliantCount = crewStats.compliant || 0;     // Compliant across ALL crew
  const warningsCount = crewStats.warnings || 0;
  const issuesCount = crewStats.issues || 0;
  const totalCount = crewStats.total || 0;

  // FT-90: On Duty compliance stats (from extended stats if available)
  const onDutyCompliant = crewStats.onDutyCompliant ?? activeCount - (crewStats.onDutyIssues || 0);
  const onDutyIssues = crewStats.onDutyIssues || 0;

  // FT-90: SMS Compliance = (On Duty crew with no issues / Total On Duty) × 100
  // This is the PRIMARY compliance metric for regulatory purposes
  // MCA auditors focus on current operational crew readiness
  const smsCompliancePercentage = activeCount > 0
    ? Math.round((onDutyCompliant / activeCount) * 100)
    : 100;

  // FT-90: All Crew compliance for planning purposes (secondary)
  const allCrewCompliancePercentage = totalCount > 0
    ? Math.round((compliantCount / totalCount) * 100)
    : 100;

  // FT-90: Select which view's compliance to display
  const displayPercentage = viewMode === 'onDuty' ? smsCompliancePercentage : allCrewCompliancePercentage;
  const displayCompliant = viewMode === 'onDuty' ? onDutyCompliant : compliantCount;
  const displayTotal = viewMode === 'onDuty' ? activeCount : totalCount;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-slate-700 px-5 py-3">
        <h3 className="text-base font-semibold text-white flex items-center gap-2">
          <Users className="h-5 w-5 text-white" />
          Crew Summary
        </h3>
      </div>

      <div className="p-6">
      {/* FT-90: View Mode Toggle - On Duty (SMS) vs All Crew (Planning) */}
      <div className="flex items-center justify-center mb-4">
        <div className="inline-flex rounded-lg bg-gray-100 p-0.5">
          <button
            type="button"
            onClick={() => setViewMode('onDuty')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              viewMode === 'onDuty'
                ? 'bg-white text-cyan-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            On Duty
          </button>
          <button
            type="button"
            onClick={() => setViewMode('allCrew')}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${
              viewMode === 'allCrew'
                ? 'bg-white text-cyan-700 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            All Crew
          </button>
        </div>
      </div>

      {/* FT-90: Dynamic Compliance Bar - switches based on view mode */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm text-gray-600">
            {viewMode === 'onDuty' ? 'On Duty Compliance' : 'All Crew Compliance'}
          </span>
          <span className="text-sm font-semibold text-gray-900">{displayPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${
              displayPercentage >= 90 ? 'bg-green-600' :
              displayPercentage >= 70 ? 'bg-amber-500' : 'bg-red-600'
            }`}
            style={{ width: `${displayPercentage}%` }}
          />
        </div>
        <span className="text-xs text-gray-500 mt-1 block">
          {displayCompliant}/{displayTotal} {viewMode === 'onDuty' ? 'on duty' : 'total'} crew compliant
        </span>
      </div>

      {/* Status Counts */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-600">Active</span>
          </div>
          <span className="text-sm font-semibold text-green-600">{activeCount}</span>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-sm text-gray-600">Off Duty</span>
          </div>
          <span className="text-sm font-semibold text-gray-600">{offDutyCount}</span>
        </div>
        {onLeaveCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span className="text-sm text-gray-600">On Leave</span>
            </div>
            <span className="text-sm font-semibold text-blue-600">{onLeaveCount}</span>
          </div>
        )}
        {issuesCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-red-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Issues</span>
            </div>
            <span className="text-sm font-semibold text-red-600">{issuesCount}</span>
          </div>
        )}
        {warningsCount > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
              <span className="text-sm text-gray-600">Warnings</span>
            </div>
            <span className="text-sm font-semibold text-amber-600">{warningsCount}</span>
          </div>
        )}
      </div>

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Total</span>
          <span className="text-sm font-semibold text-gray-900">{totalCount} crew</span>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CrewSummaryCard;
